import { AxiosResponse } from 'axios';
import { store } from '../../redux/store';
import httpService from '../httpService';
import {
  Connection,
  setConnections,
  deleteConnections,
  ConnectionType,
  updateNote,
  updateMemberStatus,
  updateInitialBalance,
} from '../../redux/slices/connectionsSlice';
import { RootState } from '../../redux/reducers';
import { Currency } from '../../types/bankAccount';

/*
 * The connection service, contains all logic for connections
 */
const connectionService = {
  getConnections: async (force: boolean = false): Promise<Connection[]> => {
    if (!force && store.getState().connections.length > 0) return store.getState().connections;
    const { selectedDashboard } = (store.getState() as RootState).application;
    const { data } = await httpService.get(`/networks/dashboards/${selectedDashboard}/members`);
    store.dispatch(setConnections(data));
    return data;
  },

  deleteConnections: async (dashboardMembersIds: number[]): Promise<void> => {
    if (!dashboardMembersIds.length) return;
    const { selectedDashboard } = (store.getState() as RootState).application;
    await httpService.delete(`/networks/dashboards/${selectedDashboard}/members`, {
      data: { dashboardMembersIds },
    });
    store.dispatch(deleteConnections({ dashboardMembersIds }));
  },

  getProfilePictureUrl: async (dashboardMemberId: number): Promise<string> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService.get(`/networks/dashboards/${selectedDashboard}/members/${dashboardMemberId}/profile-picture`).then(({ data }): string => {
      return data.path;
    });
  },

  updateNote: async (id: number, note: string): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService.put(`/networks/dashboards/${selectedDashboard}/members/${id}/note`, { note }).then(() => {
      store.dispatch(updateNote({ id, type: ConnectionType.MEMBER, note }));
    });
  },

  updateInitialBalance: async (id: number, amountInCents: number, currency?: Currency): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    const searchParams = new URLSearchParams();
    if (currency) {
      searchParams.set('currency', currency);
    }
    const queryString = searchParams.toString();
    const url = `/networks/dashboards/${selectedDashboard}/members/${id}/initial-finance-balance${queryString ? `?${queryString}` : ''}`;
    return httpService.put(url, { amount: amountInCents }).then(() => {
      store.dispatch(updateInitialBalance({ id, financeAdministrationStartAmount: amountInCents }));
    });
  },

  getConnectionsWithPushToken: async (): Promise<number[]> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService.get(`/networks/dashboards/${selectedDashboard}/communication/notification/members`).then(({ data }): number[] => {
      return data;
    });
  },

  sendPushNotification: async (
    connectionIds: string[],
    message: string,
  ): Promise<AxiosResponse> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService.post(`/networks/dashboards/${selectedDashboard}/communication/notification`, { message, connectionIds });
  },

  updateMemberStatus: async (
    memberId: number,
    active: boolean,
    inactiveReason?: string,
  ): Promise<void> => {
    const dashboardId = store.getState().application.selectedDashboard;
    await httpService.put(`/networks/dashboards/${dashboardId}/members/${memberId}/status`, {
      active,
      inactiveReason,
    });
    store.dispatch(
      updateMemberStatus({
        id: memberId,
        type: ConnectionType.MEMBER,
        active,
        inactiveReason,
      }),
    );
  },

  getActiveMembers: async (): Promise<Connection[]> => {
    const dashboardId = store.getState().application.selectedDashboard;
    const { data } = await httpService.get(`/networks/dashboards/${dashboardId}/members/active`);
    return data;
  },

  getInactiveMembers: async (): Promise<Connection[]> => {
    const dashboardId = store.getState().application.selectedDashboard;
    const { data } = await httpService.get(`/networks/dashboards/${dashboardId}/members/inactive`);
    return data;
  },
};

export default connectionService;
